import { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Unlink, ImagePlus, Undo2, Redo2, Minus, Code } from "lucide-react";
import { ImagePickerDialog } from "@/components/live/ImagePickerDialog";
import { resolveUrl } from "@/lib/content";

const ToolbarButton = ({ onClick, active, disabled, label, children, testId }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled} aria-label={label} title={label} aria-pressed={active} data-testid={testId}
    className={`p-2 rounded-md transition-colors disabled:opacity-40 ${active ? "bg-jade-deep text-white" : "text-charcoal hover:bg-sand"}`}>
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, placeholder = "Write your post…" }) => {
  const [picker, setPicker] = useState(false);
  const [showHtml, setShowHtml] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-xl my-6 max-w-full h-auto" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: { attributes: { class: "rte-content prose prose-lg max-w-none focus:outline-none min-h-[280px] px-4 py-3", "data-testid": "rte-editor" } },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
  });

  // Sync external resets (e.g. opening another post) without clobbering typing
  useEffect(() => {
    if (editor && (value || "") !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove)", previous);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    const href = /^(https?:\/\/|mailto:|tel:|\/)/i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  const tb = (label, Icon, cmd, active, testId, disabled) => (
    <ToolbarButton label={label} onClick={cmd} active={active} disabled={disabled} testId={testId}><Icon className="w-4 h-4" /></ToolbarButton>
  );

  return (
    <div className="rounded-lg border border-sand bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-sand bg-sand/20 px-2 py-1.5">
        {tb("Bold", Bold, () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"), "rte-bold")}
        {tb("Italic", Italic, () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"), "rte-italic")}
        <span className="w-px h-5 bg-sand mx-1" />
        {tb("Heading", Heading2, () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive("heading", { level: 2 }), "rte-h2")}
        {tb("Subheading", Heading3, () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive("heading", { level: 3 }), "rte-h3")}
        {tb("Quote", Quote, () => editor.chain().focus().toggleBlockquote().run(), editor.isActive("blockquote"), "rte-quote")}
        <span className="w-px h-5 bg-sand mx-1" />
        {tb("Bullet list", List, () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"), "rte-bullets")}
        {tb("Numbered list", ListOrdered, () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"), "rte-numbers")}
        {tb("Divider", Minus, () => editor.chain().focus().setHorizontalRule().run(), false, "rte-hr")}
        <span className="w-px h-5 bg-sand mx-1" />
        {tb(editor.isActive("link") ? "Edit link" : "Add link", Link2, setLink, editor.isActive("link"), "rte-link")}
        {tb("Remove link", Unlink, () => editor.chain().focus().unsetLink().run(), false, "rte-unlink", !editor.isActive("link"))}
        {tb("Insert image", ImagePlus, () => setPicker(true), false, "rte-image")}
        <span className="w-px h-5 bg-sand mx-1" />
        {tb("Undo", Undo2, () => editor.chain().focus().undo().run(), false, "rte-undo", !editor.can().undo())}
        {tb("Redo", Redo2, () => editor.chain().focus().redo().run(), false, "rte-redo", !editor.can().redo())}
        <span className="flex-1" />
        {tb(showHtml ? "Visual editor" : "HTML source", Code, () => setShowHtml(!showHtml), showHtml, "rte-toggle-html")}
      </div>
      {showHtml ? (
        <textarea data-testid="rte-html-source" value={value || ""} onChange={(e) => onChange(e.target.value)} rows={14} className="w-full font-mono text-sm p-4 focus:outline-none" />
      ) : (
        <EditorContent editor={editor} />
      )}
      <ImagePickerDialog open={picker} onClose={() => setPicker(false)} title="Insert image"
        onSelect={({ url, alt }) => editor.chain().focus().setImage({ src: resolveUrl(url), alt: alt || "" }).run()} />
    </div>
  );
};
